import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'

let skeletonSx = { bgcolor: 'rgba(255,255,255,0.1)' }

type Props = {
  // Mirrors SessionRow's showThumbnail so the loading rail matches the loaded one.
  showThumbnail?: boolean
}

let SessionRowSkeleton = ({ showThumbnail = true }: Props) => (
  <Box className="relative mb-4">
    <Box
      className="relative grid overflow-hidden rounded"
      sx={{
        gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Left column: same rail shape as SessionRow — thumbnail behind (sm) or beside (xs) the date badge */}
      <Box
        className="relative flex"
        sx={{
          bgcolor: 'background.paperDark',
          borderStyle: 'solid',
          borderColor: 'divider',
          borderWidth: { xs: '0 0 1px 0', sm: '0 1px 0 0' },
        }}
      >
        {showThumbnail && (
          <Box
            className="overflow-hidden shrink-0"
            sx={{
              position: { xs: 'relative', sm: 'absolute' },
              inset: { sm: 0 },
              width: { xs: 112, sm: 'auto' },
              aspectRatio: { xs: '16/9', sm: 'auto' },
            }}
          >
            <Skeleton
              variant="rectangular"
              animation="wave"
              sx={{
                ...skeletonSx,
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        )}
        <Skeleton
          variant="rounded"
          width={56}
          height={40}
          animation="wave"
          className="self-start"
          sx={{
            ...skeletonSx,
            position: { sm: 'absolute' },
            top: { sm: 6 },
            left: { sm: 6 },
            m: { xs: 1, sm: 0 },
          }}
        />
      </Box>

      {/* Right column: text lines */}
      <Box className="min-w-0 py-3 pl-3" sx={{ pr: '52px' }}>
        <Box className="flex items-center gap-2" sx={{ mb: 0.75 }}>
          <Skeleton
            variant="text"
            width="55%"
            height={13}
            animation="wave"
            sx={skeletonSx}
          />
          <Skeleton
            variant="text"
            width="18%"
            height={13}
            animation="wave"
            sx={skeletonSx}
          />
        </Box>
        <Skeleton
          variant="text"
          width="75%"
          height={24}
          animation="wave"
          sx={{ ...skeletonSx, mb: 0.5 }}
        />
        <Box className="flex flex-wrap items-center gap-3" sx={{ mb: 0.75 }}>
          <Skeleton
            variant="text"
            width={120}
            height={16}
            animation="wave"
            sx={skeletonSx}
          />
          <Skeleton
            variant="text"
            width={100}
            height={16}
            animation="wave"
            sx={skeletonSx}
          />
        </Box>
        <Skeleton
          variant="text"
          width="40%"
          height={14}
          animation="wave"
          sx={skeletonSx}
        />
      </Box>
    </Box>
  </Box>
)

export default SessionRowSkeleton
